import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "../context/auth";

type ApiKey = {
	id: string;
	keyPrefix: string;
	name: string;
	scopes: string[];
	createdBy: string;
	lastUsedAt: string | null;
	expiresAt: string | null;
	revokedAt: string | null;
	createdAt: string;
	updatedAt: string;
};

function formatDate(dateStr: string): string {
	return new Date(dateStr).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

function errorMessageForStatus(status: number): string {
	switch (status) {
		case 404:
			return "API key not found. It may have been revoked.";
		case 401:
			return "Session expired. Please sign in again.";
		case 403:
			return "You don't have permission to perform this action.";
		default:
			return `Something went wrong (${status}). Please try again.`;
	}
}

export function ApiKeysPage() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const { hasPermission } = useAuth();

	const [keys, setKeys] = useState<ApiKey[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [name, setName] = useState("");
	const [scopesInput, setScopesInput] = useState("");
	const [expiresAt, setExpiresAt] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [newPlaintext, setNewPlaintext] = useState<string | null>(null);
	const [rotatedPlaintext, setRotatedPlaintext] = useState<{
		keyId: string;
		plaintext: string;
	} | null>(null);

	const fetchKeys = useCallback(
		async (signal?: AbortSignal) => {
			try {
				setError(null);
				const res = await fetch(`/api/tenant/${workspaceSlug}/keys`, {
					credentials: "include",
					signal,
				});
				if (!res.ok) {
					throw new Error(errorMessageForStatus(res.status));
				}
				const data = (await res.json()) as { keys: ApiKey[] };
				setKeys(data.keys);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setError(
					err instanceof Error ? err.message : "Failed to load API keys",
				);
			} finally {
				setLoading(false);
			}
		},
		[workspaceSlug],
	);

	useEffect(() => {
		const controller = new AbortController();
		fetchKeys(controller.signal);
		return () => controller.abort();
	}, [fetchKeys]);

	async function handleCreate(e: { preventDefault: () => void }) {
		e.preventDefault();
		setSubmitting(true);
		setError(null);
		setNewPlaintext(null);

		const scopes = scopesInput
			.split(",")
			.map((s) => s.trim())
			.filter(Boolean);

		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/keys`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					name,
					scopes,
					...(expiresAt
						? { expiresAt: new Date(expiresAt).toISOString() }
						: {}),
				}),
			});
			if (!res.ok) {
				throw new Error(errorMessageForStatus(res.status));
			}
			const data = (await res.json()) as ApiKey & { plaintext: string };
			setNewPlaintext(data.plaintext);
			setName("");
			setScopesInput("");
			setExpiresAt("");
			await fetchKeys();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to create API key");
		} finally {
			setSubmitting(false);
		}
	}

	async function handleRotate(id: string) {
		setError(null);
		setRotatedPlaintext(null);
		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/keys/${id}/rotate`,
				{
					method: "POST",
					credentials: "include",
				},
			);
			if (!res.ok) {
				throw new Error(errorMessageForStatus(res.status));
			}
			const data = (await res.json()) as ApiKey & { plaintext: string };
			setRotatedPlaintext({ keyId: id, plaintext: data.plaintext });
			await fetchKeys();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to rotate key");
		}
	}

	async function handleRevoke(id: string) {
		if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
		setError(null);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/keys/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!res.ok) {
				throw new Error(errorMessageForStatus(res.status));
			}
			setRotatedPlaintext(null);
			await fetchKeys();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to revoke key");
		}
	}

	if (!hasPermission("api-keys", "read")) {
		return (
			<div className="max-w-2xl mx-auto mt-8">
				<p className="text-sm text-gray-600" data-testid="apikeys-no-access">
					You don't have access to this page.
				</p>
			</div>
		);
	}

	return (
		<div className="max-w-3xl mx-auto mt-8" data-testid="apikeys-page">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">API Keys</h1>

			{error && (
				<div
					className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 mb-4"
					data-testid="apikeys-error"
				>
					{error}
				</div>
			)}

			{newPlaintext && (
				<div
					className="bg-green-50 border border-green-200 rounded-md p-3 mb-4"
					data-testid="plaintext-container"
				>
					<p className="text-xs font-medium text-green-800 mb-1">
						This key won't be shown again. Copy it now.
					</p>
					<input
						type="text"
						readOnly
						value={newPlaintext}
						onClick={(e) => (e.target as HTMLInputElement).select()}
						className="w-full text-sm bg-white border border-green-300 rounded px-2 py-1 font-mono text-green-900 focus:outline-none"
						data-testid="plaintext-input"
					/>
				</div>
			)}

			<section className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
				<h2 className="text-lg font-semibold text-gray-900 mb-4">
					Active Keys
				</h2>

				{loading ? (
					<div className="text-sm text-gray-500" data-testid="apikeys-loading">
						Loading API keys...
					</div>
				) : keys.length === 0 ? (
					<p className="text-sm text-gray-400 text-center py-6">
						No API keys configured.
					</p>
				) : (
					<table className="w-full text-sm text-left">
						<thead>
							<tr className="border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
								<th className="py-2 pr-4 font-medium">Name</th>
								<th className="py-2 pr-4 font-medium">Key Prefix</th>
								<th className="py-2 pr-4 font-medium">Scopes</th>
								<th className="py-2 pr-4 font-medium">Created</th>
								<th className="py-2 pr-4 font-medium">Last Used</th>
								<th className="py-2 pr-4 font-medium">Expires</th>
								<th className="py-2 font-medium">Actions</th>
							</tr>
						</thead>
						<tbody>
							{keys.map((key) => (
								<tr
									key={key.id}
									className="border-b border-gray-100"
									data-testid="apikey-row"
								>
									<td className="py-3 pr-4 text-gray-900 text-xs">
										{key.name}
									</td>
									<td className="py-3 pr-4 text-gray-600 font-mono text-xs">
										{key.keyPrefix}...
									</td>
									<td className="py-3 pr-4 text-gray-600 text-xs">
										{key.scopes.length > 0 ? key.scopes.join(", ") : "all"}
									</td>
									<td className="py-3 pr-4 text-gray-500 text-xs">
										{formatDate(key.createdAt)}
									</td>
									<td className="py-3 pr-4 text-gray-500 text-xs">
										{key.lastUsedAt ? formatDate(key.lastUsedAt) : "Never"}
									</td>
									<td className="py-3 pr-4 text-gray-500 text-xs">
										{key.expiresAt ? formatDate(key.expiresAt) : "Never"}
									</td>
									<td className="py-3">
										<div className="flex items-center gap-2">
											{hasPermission("api-keys", "delete") && (
												<>
													<button
														type="button"
														onClick={() => handleRotate(key.id)}
														className="text-blue-600 hover:text-blue-800 text-xs cursor-pointer"
														data-testid="rotate-key-btn"
													>
														Rotate
													</button>
													<button
														type="button"
														onClick={() => handleRevoke(key.id)}
														className="text-red-600 hover:text-red-800 text-xs cursor-pointer"
														data-testid="revoke-key-btn"
													>
														Revoke
													</button>
												</>
											)}
										</div>
										{rotatedPlaintext?.keyId === key.id && (
											<div
												className="bg-green-50 border border-green-200 rounded-md p-2 mt-2"
												data-testid="plaintext-container"
											>
												<p className="text-xs font-medium text-green-800 mb-1">
													This key won't be shown again. Copy it now.
												</p>
												<input
													type="text"
													readOnly
													value={rotatedPlaintext.plaintext}
													onClick={(e) =>
														(e.target as HTMLInputElement).select()
													}
													className="w-full text-xs bg-white border border-green-300 rounded px-2 py-1 font-mono text-green-900 focus:outline-none"
													data-testid="rotated-plaintext-input"
												/>
											</div>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</section>

			{hasPermission("api-keys", "create") && (
				<section className="bg-white border border-gray-200 rounded-lg p-6">
					<h2 className="text-lg font-semibold text-gray-900 mb-4">
						Create API Key
					</h2>
					<form onSubmit={handleCreate} data-testid="create-key-form">
						<div className="mb-4">
							<label
								htmlFor="key-name"
								className="block text-xs font-medium text-gray-600 mb-1"
							>
								Name
							</label>
							<input
								id="key-name"
								type="text"
								required
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="My API Key"
								className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
								data-testid="key-name-input"
							/>
						</div>
						<div className="mb-4">
							<label
								htmlFor="key-scopes"
								className="block text-xs font-medium text-gray-600 mb-1"
							>
								Scopes (comma-separated, e.g. data:read, data:write)
							</label>
							<input
								id="key-scopes"
								type="text"
								value={scopesInput}
								onChange={(e) => setScopesInput(e.target.value)}
								placeholder="data:read, data:write"
								className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
								data-testid="key-scopes-input"
							/>
						</div>
						<div className="mb-4">
							<label
								htmlFor="key-expires"
								className="block text-xs font-medium text-gray-600 mb-1"
							>
								Expires (optional)
							</label>
							<input
								id="key-expires"
								type="date"
								value={expiresAt}
								onChange={(e) => setExpiresAt(e.target.value)}
								className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
								data-testid="key-expires-input"
							/>
						</div>
						<button
							type="submit"
							disabled={submitting || !name}
							className="bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 cursor-pointer"
							data-testid="create-key-btn"
						>
							{submitting ? "Creating..." : "Create Key"}
						</button>
					</form>
				</section>
			)}
		</div>
	);
}
